if (!customElements.get('purchase-row-price')) {
  customElements.define(
    'purchase-row-price',
    class PurchaseRowPrice extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        const productInfo = this.closest('product-info');
        this.originalSectionId =
          productInfo?.dataset.originalSection ||
          (this.sectionId?.startsWith('quickadd-') ? this.sectionId.slice('quickadd-'.length) : this.sectionId);
      }

      matchesSection(eventSectionId) {
        return eventSectionId === this.sectionId || eventSectionId === this.originalSectionId;
      }

      connectedCallback() {
        this.priceRoot = this.querySelector('.price');
        this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);

        if (!this.priceRoot || !this.quantityInput) return;

        this.syncFromDataset();
        this.updateTotalPrice = this.updateTotalPrice.bind(this);

        this.quantityInput.addEventListener('change', this.updateTotalPrice);
        this.quantityInput.addEventListener('input', this.updateTotalPrice);

        if (typeof subscribe !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!this.matchesSection(event.data?.sectionId)) return;

            const variant = event.data.variant;
            if (variant) {
              this.dataset.unitPrice = variant.price;
              this.dataset.compareAtPrice = variant.compare_at_price || 0;
              this.priceBreaks = variant.quantity_price_breaks || [];
            }

            requestAnimationFrame(() => {
              this.priceRoot = this.querySelector('.price');
              this.syncFromDataset();
              this.updateTotalPrice();
            });
          });

          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.updateTotalPrice);
        }

        this.updateTotalPrice();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
      }

      syncFromDataset() {
        this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
        this.compareAtPrice = parseInt(this.dataset.compareAtPrice, 10) || 0;
        this.currency = this.dataset.currency || 'ILS';
        this.volumePricing = this.dataset.volumePricing === 'true';

        if (!this.priceBreaks?.length && this.dataset.priceBreaks) {
          try {
            this.priceBreaks = JSON.parse(this.dataset.priceBreaks);
          } catch (error) {
            this.priceBreaks = [];
          }
        }

        if (!this.priceBreaks) {
          this.priceBreaks = [];
        }
      }

      getUnitPriceForQuantity(quantity) {
        if (this.volumePricing && this.priceBreaks.length) {
          const sorted = [...this.priceBreaks].sort((a, b) => b.minimum_quantity - a.minimum_quantity);
          for (const priceBreak of sorted) {
            if (quantity >= priceBreak.minimum_quantity) {
              return priceBreak.price;
            }
          }
        }

        return this.unitPrice;
      }

      formatMoney(cents) {
        const amount = cents / 100;
        const locale = document.documentElement.lang || 'he';
        const rounded = Math.round(amount);

        try {
          const parts = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).formatToParts(rounded);

          const currency = parts.find((part) => part.type === 'currency')?.value || '₪';
          const formattedAmount = parts
            .filter((part) => part.type === 'integer' || part.type === 'group')
            .map((part) => part.value)
            .join('');

          return { currency, amount: formattedAmount || String(rounded) };
        } catch (error) {
          return { currency: '₪', amount: String(rounded) };
        }
      }

      setPurchaseRowPrice(priceItem, cents) {
        if (!priceItem) return false;

        const formatted = this.formatMoney(cents);
        const currencyEl = priceItem.querySelector('.price-currency');
        const amountEl = priceItem.querySelector('.price-amount');

        if (!currencyEl || !amountEl) return false;

        currencyEl.textContent = formatted.currency;
        amountEl.textContent = formatted.amount;
        return true;
      }

      updateTotalPrice() {
        this.priceRoot = this.querySelector('.price');
        if (!this.priceRoot) return;

        const quantity = parseInt(this.quantityInput.value, 10);
        if (!quantity || quantity < 1) return;

        const unitPrice = this.getUnitPriceForQuantity(quantity);
        const totalPrice = unitPrice * quantity;
        const compareUnit = this.compareAtPrice > unitPrice ? this.compareAtPrice : 0;
        const compareTotal = compareUnit * quantity;

        const isOnSale = this.priceRoot.classList.contains('price--on-sale');
        const salePriceItem = this.priceRoot.querySelector('.price-item--sale.price-item--purchase-row, .price-item--sale');
        const regularPriceItems = this.priceRoot.querySelectorAll('.price-item--regular');

        if (isOnSale && salePriceItem) {
          if (!this.setPurchaseRowPrice(salePriceItem, totalPrice)) {
            salePriceItem.textContent = `${this.formatMoney(totalPrice).currency} ${this.formatMoney(totalPrice).amount}`;
          }

          const compareItem = this.priceRoot.querySelector('.price__sale s.price-item--regular');
          if (compareItem && compareTotal) {
            compareItem.textContent = `${this.formatMoney(compareTotal).currency} ${this.formatMoney(compareTotal).amount}`;
          }
        } else {
          const mainRegular = this.priceRoot.querySelector(
            '.price__regular .price-item--regular.price-item--purchase-row, .price__regular .price-item--regular'
          );
          if (mainRegular && this.setPurchaseRowPrice(mainRegular, totalPrice)) {
            return;
          }

          if (mainRegular) {
            const formatted = this.formatMoney(totalPrice);
            mainRegular.textContent = `${formatted.currency} ${formatted.amount}`;
          } else if (regularPriceItems.length) {
            const formatted = this.formatMoney(totalPrice);
            regularPriceItems[regularPriceItems.length - 1].textContent = `${formatted.currency} ${formatted.amount}`;
          }
        }
      }
    }
  );
}
